/**
 * Copyright 2025 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 */

import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { PubSubNodeFilterFactory } from './NodeFilters';
import { OpcUaMqttPubSubContext, OpcUaMqttPubSubContextType, OpcUaMqttPubSubContextVersion } from './OpcUaMqttPubSub';
import { NodeIdentifier, PubSubAddressSpace } from './PubSubAddressSpace';
import { QualifiedName, ValueType } from './PubSubTypes';

const _tooManyNodesLastLogged = new Map<string, number>();
const _TOO_MANY_NODES_COOLDOWN_MS = 60_000;

function _logTooManyNodes(key: string, ...args: unknown[]): void {
  const now = Date.now();
  const last = _tooManyNodesLastLogged.get(key) ?? 0;
  if (now - last >= _TOO_MANY_NODES_COOLDOWN_MS) {
    _tooManyNodesLastLogged.set(key, now);
    console.warn('Too many nodes for the request. Expected only max. 1.', key, ...args);
  }
}

export type ObjectMap<T> = { [key: string]: T };
export type ObjectMapWithKeys<T, U> = { [p in keyof U]: T };

export function useAddressSpaceContext(): OpcUaMqttPubSubContextType | undefined {
  return useContext<OpcUaMqttPubSubContextType | undefined>(OpcUaMqttPubSubContext);
}

export function useAddressSpaceVersion(): number {
  return useContext<number>(OpcUaMqttPubSubContextVersion);
}

function GetNodeVersions(adSpace: PubSubAddressSpace | undefined, nodeIds: (NodeIdentifier | undefined)[]) {
  if (adSpace === undefined) {
    return [];
  }
  let ret: (number | undefined)[] = [];
  for (const n of nodeIds) {
    if (n === undefined) {
      ret.push(undefined);
    } else {
      ret.push(adSpace.getState().getNodeVersionById(n));
    }
  }
  return ret;
}

// Overload signatures, so typescript knows the correct types
export function useBrowseName(nodeIdentifier: NodeIdentifier): QualifiedName | undefined;
export function useBrowseName(nodeIdentifier: NodeIdentifier[]): (QualifiedName | undefined)[];
export function useBrowseName<K extends ObjectMap<NodeIdentifier>>(
  nodeIdentifier: K,
): ObjectMapWithKeys<QualifiedName, K>;
export function useBrowseName(
  nodeIdentifiers: NodeIdentifier | NodeIdentifier[] | ObjectMap<NodeIdentifier>,
): (QualifiedName | undefined) | (QualifiedName | undefined)[] | ObjectMap<QualifiedName> {
  let hasArray = true;
  let sortedObjectKeys: string[] | undefined;
  if (!Array.isArray(nodeIdentifiers)) {
    if (typeof nodeIdentifiers === 'object') {
      sortedObjectKeys = Object.keys(nodeIdentifiers).sort();
      nodeIdentifiers = sortedObjectKeys.map((k) => nodeIdentifiers[k]);
    } else {
      hasArray = false;
      nodeIdentifiers = [nodeIdentifiers];
    }
  }
  const adSpace = useAddressSpaceContext();

  const nsIdxs = useMemo<(QualifiedName | undefined)[]>(() => {
    let normalizedIdentifiers: string[] = [];

    if (Array.isArray(nodeIdentifiers)) {
      normalizedIdentifiers = nodeIdentifiers;
    } else if (typeof nodeIdentifiers === 'string') {
      normalizedIdentifiers = [nodeIdentifiers];
    } else if (nodeIdentifiers) {
      normalizedIdentifiers = Object.values(nodeIdentifiers);
    }

    if (adSpace === undefined) {
      return new Array(normalizedIdentifiers.length).fill(undefined);
    }

    return normalizedIdentifiers.map((nId) => adSpace.addrSpaceStore.getState().getNodeBrowseNameById(nId));
  }, [adSpace, nodeIdentifiers]);

  if (sortedObjectKeys !== undefined) {
    let ret: ObjectMap<QualifiedName> = {};
    for (const [i, k] of sortedObjectKeys.entries()) {
      const browseName = nsIdxs[i];

      if (browseName !== undefined) {
        ret[k] = browseName;
      }
    }
    return ret;
  }

  return hasArray ? (nsIdxs as QualifiedName[]) : (nsIdxs[0] as QualifiedName);
}

// Overload signatures, so typescript knows the correct types
export function useNamespaceIndex(nsUris: string): number | undefined;
export function useNamespaceIndex(nsUris: string[]): (number | undefined)[];
export function useNamespaceIndex<K extends ObjectMap<string>>(nsUris: K): ObjectMapWithKeys<number, K>;
export function useNamespaceIndex(
  nsUris: string | string[] | ObjectMap<string>,
): (number | undefined) | (number | undefined)[] | ObjectMap<number> {
  let hasArray = true;
  let sortedObjectKeys: string[] | undefined;
  if (!Array.isArray(nsUris)) {
    if (typeof nsUris === 'object') {
      sortedObjectKeys = Object.keys(nsUris).sort();
      nsUris = sortedObjectKeys.map((k) => nsUris[k]);
    } else {
      hasArray = false;
      nsUris = [nsUris];
    }
  }
  const adSpace = useAddressSpaceContext();
  const nsIdxs = useMemo<(number | undefined)[]>(() => {
    let normalizedUris: string[] = [];

    if (Array.isArray(nsUris)) {
      normalizedUris = nsUris;
    } else if (typeof nsUris === 'string') {
      normalizedUris = [nsUris];
    } else if (nsUris) {
      normalizedUris = Object.values(nsUris);
    }

    if (adSpace === undefined) {
      return new Array(normalizedUris.length).fill(undefined);
    }

    return normalizedUris.map((uri) => adSpace.addrSpaceStore.getState().getNamespaceIdByUri(uri));
  }, [adSpace, nsUris]);

  if (sortedObjectKeys !== undefined) {
    let ret: ObjectMap<number> = {};
    for (const [i, k] of sortedObjectKeys.entries()) {
      const nsId = nsIdxs[i];

      if (nsId !== undefined) {
        ret[k] = nsId;
      }
    }
    return ret;
  }

  // 4. Type-Assertion für die Rückgabe, um undefined-Werte bei asynchronem Laden zu überbrücken
  return hasArray ? (nsIdxs as number[]) : (nsIdxs[0] as number);
}

function resolveNodeFilterPath(
  filters: PubSubNodeFilterFactory | PubSubNodeFilterFactory[],
  adSpace: PubSubAddressSpace,
  startingNodeId: NodeIdentifier,
): [string[], string[]] {
  let currentNodeId: string | undefined = startingNodeId;
  let resolvedNodePath = [startingNodeId];
  if (!Array.isArray(filters)) {
    filters = [filters];
  }
  for (const [index, filterFactory] of filters.entries()) {
    const filter = filterFactory(adSpace);
    const matchingNodeIds = adSpace.getState().getNodeIdsByFilter(filter, currentNodeId);

    if (index === filters.length - 1) {
      // last element, keep array
      if (currentNodeId !== undefined) {
        resolvedNodePath.push(currentNodeId);
      }
      return [matchingNodeIds, resolvedNodePath];
    }

    if (matchingNodeIds.length === 0) {
      currentNodeId = undefined;
      break;
    }

    if (matchingNodeIds.length > 1) {
      /// TODO how to handle errors
      _logTooManyNodes(`_resolveFilter:intermediate:${startingNodeId}`, matchingNodeIds);
    }

    // Keep the previous fallback behavior by taking the first match for intermediate path steps.
    currentNodeId = matchingNodeIds[0];
    resolvedNodePath.push(currentNodeId);
  }
  return [[], resolvedNodePath];
}

// export function useNamespaceIndex<K extends ObjectMap<string> >(nsUris: K) : ObjectMapWithKeys<number, K>;

export function usePubSubNodes(
  filters: PubSubNodeFilterFactory[] | PubSubNodeFilterFactory,
  startingNodeId?: NodeIdentifier,
  dependencies?: any[],
): NodeIdentifier[];
export function usePubSubNodes<K extends ObjectMap<PubSubNodeFilterFactory[] | PubSubNodeFilterFactory>>(
  filters: K,
  startingNodeId?: NodeIdentifier,
  dependencies?: any[],
): ObjectMapWithKeys<NodeIdentifier[], K>;
// Resolves filters to a single node
export function usePubSubNodes(
  filters:
    | PubSubNodeFilterFactory[]
    | PubSubNodeFilterFactory
    | ObjectMap<PubSubNodeFilterFactory[] | PubSubNodeFilterFactory>,
  startingNodeId?: NodeIdentifier,
  dependencies?: any[],
): NodeIdentifier[] | ObjectMap<NodeIdentifier[]> {
  if (!Array.isArray(dependencies)) {
    dependencies = [];
  }
  const adSpace = useAddressSpaceContext();
  const adSpaceVersion = useAddressSpaceVersion();

  const [nodePath, setNodePath] = useState<NodeIdentifier[]>([]);

  const nodeVersions = GetNodeVersions(adSpace?.addrSpaceStore, nodePath);
  /// todo use State variable with offset to start always at zero?
  const sumNodeVersions = nodeVersions.reduce((prev: number, curr) => (curr !== undefined ? prev + curr : prev), 0);

  const resolvedFilters = useMemo(() => {
    if (adSpace === undefined || !adSpace.addrSpaceStore.getState().isAddressSpaceReconstructed()) {
      if (!Array.isArray(filters) && typeof filters === 'object') {
        return Object.assign({}, ...Object.keys(filters).map((k) => ({ [k]: [] })));
      } else {
        return [];
      }
    }
    const startingNodeIdDef =
      startingNodeId === undefined ? adSpace.addrSpaceStore.getState().getRootNodeId() : startingNodeId;
    let resolvedNodeIds: NodeIdentifier[] | ObjectMap<NodeIdentifier[]> = {};
    if (!Array.isArray(filters) && typeof filters === 'object') {
      let nodePaths: string[][] = [];
      for (const k in filters) {
        const [matchingNodeIds, nodePath] = resolveNodeFilterPath(
          filters[k],
          adSpace.addrSpaceStore,
          startingNodeIdDef,
        );
        resolvedNodeIds[k] = matchingNodeIds;
        nodePaths.push(nodePath);
      }
      setNodePath(nodePaths.flat());
    } else {
      const [matchingNodeIds, nodePath] = resolveNodeFilterPath(filters, adSpace.addrSpaceStore, startingNodeIdDef);
      resolvedNodeIds = matchingNodeIds;
      setNodePath(nodePath);
    }
    return resolvedNodeIds;
  }, [adSpace, adSpaceVersion, startingNodeId, sumNodeVersions, dependencies.length, ...dependencies]);

  return resolvedFilters;
}

function UsePubSubValue(nodeIds: (NodeIdentifier | undefined)[]): (ValueType | undefined)[] {
  const adSpace = useAddressSpaceContext();

  const arrUndefined = Array(nodeIds.length).fill(undefined);
  const [values, setValues] = useState<(ValueType | undefined)[]>(arrUndefined);
  // Trigger to update values, this collects all updates and updates the values only once
  const [requireUpdate, setRequireUpdate] = useState<number>(0);

  const requestValueUpdate = useCallback(() => {
    setRequireUpdate((prev) => prev + 1);
  }, []);

  const updateValues = useCallback(() => {
    if (adSpace === undefined) {
      const arrUndefined = Array(nodeIds.length).fill(undefined);
      setValues(arrUndefined);
      return;
    }
    const newValues = nodeIds.map((id) =>
      id !== undefined ? adSpace.addrSpaceStore.getState().getNodeValue(id) : undefined,
    );
    setValues(newValues);
  }, [adSpace, nodeIds.join('--')]);

  useEffect(() => {
    const unsubscribe = nodeIds.map((id) => {
      if (id === undefined) {
        return undefined;
      }
      return adSpace?.addrSpaceStore.getState().subscribeToNodeValueChange(id, requestValueUpdate);
    });
    return () => {
      unsubscribe.forEach((unsub) => unsub?.());
    };
  }, [adSpace, requestValueUpdate]);

  useEffect(() => {
    updateValues();
  }, [updateValues, requireUpdate]);

  return values;
}

export function usePubSubValue(nodeIds: NodeIdentifier | undefined): ValueType | undefined;
export function usePubSubValue(nodeIds: (NodeIdentifier | undefined)[]): (ValueType | undefined)[];
export function usePubSubValue<K extends ObjectMap<NodeIdentifier | undefined>>(
  nodeIds: K,
): ObjectMapWithKeys<ValueType | undefined, K>;
export function usePubSubValue(
  nodeIds: (NodeIdentifier | undefined)[] | ObjectMap<NodeIdentifier | undefined> | (NodeIdentifier | undefined),
): (ValueType | undefined)[] | ObjectMap<ValueType | undefined> {
  if (Array.isArray(nodeIds)) {
    return UsePubSubValue(nodeIds);
  } else if (typeof nodeIds === 'object') {
    const sortedKeys = Object.keys(nodeIds).sort();
    const nodeIdArray = sortedKeys.map((k) => nodeIds[k]);
    const values = UsePubSubValue(nodeIdArray);
    let ret: ObjectMap<ValueType | undefined> = {};
    for (const [i, k] of sortedKeys.entries()) {
      ret[k] = values[i];
    }
    return ret;
  } else {
    const values = UsePubSubValue([nodeIds]);
    return values[0];
  }
}

export function usePubSubNode(
  filters: PubSubNodeFilterFactory[] | PubSubNodeFilterFactory,
  startingNodeId?: NodeIdentifier,
  dependencies?: any[],
): NodeIdentifier | undefined;
export function usePubSubNode<K extends ObjectMap<PubSubNodeFilterFactory[] | PubSubNodeFilterFactory>>(
  filters: K,
  startingNodeId?: NodeIdentifier,
  dependencies?: any[],
): ObjectMapWithKeys<NodeIdentifier | undefined, K>;
// Resolves filters to a single node
export function usePubSubNode(
  filters:
    | PubSubNodeFilterFactory[]
    | PubSubNodeFilterFactory
    | ObjectMap<PubSubNodeFilterFactory[] | PubSubNodeFilterFactory>,
  startingNodeId?: NodeIdentifier,
  dependencies?: any[],
): NodeIdentifier | undefined | ObjectMap<NodeIdentifier | undefined> {
  const nodes = usePubSubNodes(filters as any, startingNodeId, dependencies);
  if ((Array.isArray(filters) || typeof filters !== 'object') && Array.isArray(nodes)) {
    if (nodes.length > 1) {
      _logTooManyNodes(`usePubSubNode:${startingNodeId ?? 'root'}`, nodes);
    } else if (nodes.length === 0) {
      return undefined;
    }
    return nodes[0];
  } else {
    const resolvedNodes: ObjectMap<NodeIdentifier | undefined> = {};
    for (const key in nodes) {
      if (nodes[key].length > 1) {
        _logTooManyNodes(`${key}:${startingNodeId ?? 'root'}`, nodes[key]);
        resolvedNodes[key] = nodes[key][0];
      } else if (nodes[key].length === 0) {
        resolvedNodes[key] = undefined;
      } else {
        resolvedNodes[key] = nodes[key][0];
      }
    }
    return resolvedNodes;
  }
}
